import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { itemKey, lineTax, http } from "../lib/api";

const StoreContext = createContext(null);

const load = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key)) || [];
  } catch {
    return [];
  }
};

const pick = (p) => {
  const v = p.variants?.find((x) => x.key === p.variant) || p.variants?.[0];
  return {
    slug: p.slug, name: p.name, price: p.price, mrp: p.mrp, image: v?.images?.[0] || p.images?.[0] || p.image,
    build_minutes: p.build_minutes, tagline: p.tagline, variant: v?.key || p.variant || null,
    variant_name: v?.name || p.variant_name || null, gst_exclusive: !!p.gst_exclusive, gst_rate: p.gst_rate || 0,
  };
};

export const StoreProvider = ({ children }) => {
  const [cart, setCart] = useState(() => load("hw_cart"));
  const [wishlist, setWishlist] = useState(() => load("hw_wishlist"));
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => localStorage.setItem("hw_cart", JSON.stringify(cart)), [cart]);
  useEffect(() => localStorage.setItem("hw_wishlist", JSON.stringify(wishlist)), [wishlist]);
  useEffect(() => {
    http.get("/products").then(({ data }) => {
      const live = new Map(data.map((p) => [p.slug, p]));
      const fresh = (x) => {
        const p = live.get(x.slug);
        const v = p.variants?.find((y) => y.key === x.variant);
        return { ...x, image: (v?.images || p.images)[0] };
      };
      setCart((c) => c.filter((x) => live.has(x.slug)).map(fresh));
      setWishlist((w) => w.filter((x) => live.has(x.slug)).map(fresh));
    }).catch(() => {});
  }, []);

  const addToCart = (products, { open = true } = {}) => {
    const list = Array.isArray(products) ? products : [products];
    setCart((c) => {
      let next = [...c];
      list.forEach((p) => {
        const item = pick(p);
        const i = next.findIndex((x) => itemKey(x) === itemKey(item));
        if (i >= 0) next[i] = { ...next[i], qty: Math.min(10, next[i].qty + (p.qty || 1)) };
        else next.push({ ...item, qty: p.qty || 1 });
      });
      return next;
    });
    toast.success(list.length > 1 ? `${list.length} pieces added to cart` : `${list[0].name} added to cart`);
    if (open) setCartOpen(true);
  };

  const updateQty = (key, qty) =>
    setCart((c) => c.map((x) => (itemKey(x) === key ? { ...x, qty: Math.max(1, Math.min(10, qty)) } : x)));
  const removeFromCart = (key) => setCart((c) => c.filter((x) => itemKey(x) !== key));
  const clearCart = () => setCart([]);

  const inWishlist = (slug) => wishlist.some((x) => x.slug === slug);
  const toggleWishlist = (p) => {
    if (inWishlist(p.slug)) {
      setWishlist((w) => w.filter((x) => x.slug !== p.slug));
      toast(`Removed ${p.name} from wishlist`);
    } else {
      setWishlist((w) => [...w, pick(p)]);
      toast.success(`Saved ${p.name} to wishlist`);
    }
  };

  const count = cart.reduce((s, x) => s + x.qty, 0);
  const subtotal = cart.reduce((s, x) => s + x.qty * x.price, 0);
  const mrpTotal = cart.reduce((s, x) => s + x.qty * x.mrp, 0);
  const taxBase = cart.reduce((s, x) => s + lineTax(x), 0);

  return (
    <StoreContext.Provider
      value={{ cart, wishlist, cartOpen, setCartOpen, addToCart, updateQty, removeFromCart, clearCart,
        inWishlist, toggleWishlist, count, subtotal, mrpTotal, taxBase }}
    >
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => useContext(StoreContext);
